import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { AppWrap, MotionWrap } from '../../wrapper';
import { urlFor, client } from '../../client';
import './Skills.scss';
import { images } from '../../constants';

const Skills = () => {
  const [experiences, setExperiences] = useState([]);
  const [skills, setSkills] = useState([]);
  const [activeDescs, setActiveDescs] = useState([]);
  const [beatFast, setBeatFast] = useState(true); // Caret fast beat
  const [resumeBeatFast, setResumeBeatFast] = useState(true); // Resume button fast beat
  const [resumeClicked, setResumeClicked] = useState(false); // Resume clicked

  useEffect(() => {
    const query = '*[_type == "experiences"]';
    const skillsQuery = '*[_type == "skills"]';

    client.fetch(query).then((data) => setExperiences(data));
    client.fetch(skillsQuery).then((data) => setSkills(data));

    // Set timer to slow down heartbeat after 4 seconds
    const timer = setTimeout(() => {
      setBeatFast(false);
      setResumeBeatFast(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  const toggleDesc = (key) => {
    setActiveDescs((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  experiences.sort((a, b) => (a.imp > b.imp ? 1 : -1));
  skills.sort((a, b) => (a.imp > b.imp ? 1 : -1));

  return (
    <>
      <h2 className="head-text">
        Skills & <span>Experiences</span>
      </h2>

      <div className="app__skills-container">
        <motion.div className="app__skills-list">
          {skills.map((skill) => (
            <motion.div
              whileInView={{ opacity: [0, 1] }}
              transition={{ duration: 0.5 }}
              className="app__skills-item app__flex"
              key={skill.name}
            >
              <div className="app__flex" style={{ backgroundColor: skill.bgColor }}>
                <img src={urlFor(skill.icon)} alt={skill.name} />
              </div>
              <p className="p-text">{skill.name}</p>
            </motion.div>
          ))}
        </motion.div>

        <div className="app__skills-exp">
          {experiences.map((experience, expIndex) => (
            <motion.div className="app__skills-exp-item" key={experience.name}>
              <div className="app__skills-exp-year">
                <p className="bold-text">{experience.year}</p>
              </div>
              <motion.div className="app__skills-exp-works">
                {experience.works.map((work, workIndex) => {
                  const uniqueKey = `${expIndex}-${workIndex}`;
                  const isOpen = activeDescs.includes(uniqueKey);

                  return (
                    <motion.div
                      whileInView={{ opacity: [0, 1] }}
                      transition={{ duration: 0.5 }}
                      className="app__skills-exp-work"
                      key={uniqueKey}
                    >
                      <div
                        className={`work-title-clickable ${isOpen ? 'active' : ''}`}
                        onClick={() => toggleDesc(uniqueKey)}
                      >
                        <h4 className="bold-text">{work.name}</h4>
                        <motion.svg
                          className={`caret-svg ${isOpen ? 'rotated' : ''}`}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          animate={
                            !isOpen
                              ? {
                                  scale: [1, 1.2, 1],
                                  filter: [
                                    'drop-shadow(0 0 0px #ffdd1f)',
                                    'drop-shadow(0 0 8px #ffdd1f)',
                                    'drop-shadow(0 0 0px #ffdd1f)',
                                  ],
                                }
                              : {
                                  scale: [1, 1.1, 0.95, 1.05, 1],
                                  rotate: [0, -5, 5, -3, 0],
                                  filter: ['drop-shadow(0 0 8px #ffdd1f)', 'drop-shadow(0 0 0px #ffdd1f)'],
                                }
                          }
                          transition={{
                            duration: isOpen 
                              ? 0.6 
                              : beatFast 
                                ? 0.8 
                                : 2,
                            repeat: isOpen ? 0 : Infinity,
                            repeatType: isOpen ? undefined : 'loop',
                            ease: 'easeInOut',
                            times: isOpen ? [0, 0.25, 0.5, 0.75, 1] : [0, 0.5, 1],
                          }}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </motion.svg>
                      </div>
                      <p className="p-text">{work.company}</p>

                      <AnimatePresence>
                        {isOpen && work.desc && (
                          <motion.p
                            className="p-text work-desc"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            {work.desc}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </motion.div>
            </motion.div>
          ))}
      <motion.a
  className="skills-btn"
  href={images.resume}
  target="_blank"
  rel="noopener noreferrer"
  onClick={() => setResumeClicked(true)}
  style={{
    boxShadow: !resumeClicked
      ? '0 0 10px #ffdd1f'
      : 'none', // manually stop shadow after click
  }}
  animate={
    !resumeClicked
      ? {
          scale: [1, 1.05, 1],
        }
      : {
          scale: [1.1, 0.95, 1.05, 1],
        }
  }
  transition={{
    duration: resumeClicked 
      ? 0.6 
      : resumeBeatFast 
        ? 0.8 
        : 2,
    repeat: resumeClicked ? 0 : Infinity,
    repeatType: resumeClicked ? undefined : 'loop',
    ease: 'easeInOut',
    times: resumeClicked ? [0, 0.25, 0.5, 0.75, 1] : [0, 0.5, 1],
  }}
>
  My Resume
</motion.a>
        </div>
      </div>
    </>
  );
};

export default AppWrap(MotionWrap(Skills, 'app__skills'), 'skills', 'app__whitebg');
